import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { FieldSelect } from "@/components/ui/field-select";
import { PLAN_LABEL, SubStatusBadge, eur, fmtDate, daysUntil, daysSince, StatCard } from "./adminUtils";
import { Loader2, CreditCard, AlertTriangle, TrendingUp, ArrowRight } from "lucide-react";

const PLAN_OPTS = [{ value: "all", label: "Tous les plans" }, ...Object.entries(PLAN_LABEL).map(([value, label]) => ({ value, label }))];
const STATUS_OPTS = [{ value: "all", label: "Tous les statuts" }, { value: "active", label: "Actif" }, { value: "past_due", label: "Impayé" }, { value: "trialing", label: "Essai" }, { value: "cancelled", label: "Résilié" }];

export default function AdminBilling() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [plan, setPlan] = useState("all"); const [status, setStatus] = useState("all");
  const [unpaidOnly, setUnpaidOnly] = useState(sp.get("unpaid") === "1");
  useEffect(() => { if (sp.get("unpaid") === "1") setUnpaidOnly(true); }, [sp]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-billing", plan, status, unpaidOnly],
    queryFn: async () => (await api.get("/api/admin/billing", { params: {
      plan: plan === "all" ? undefined : plan, status: status === "all" ? undefined : status,
      unpaid: unpaidOnly || undefined } })).data,
    retry: false, keepPreviousData: true,
  });
  const subs = data?.subscriptions || []; const unpaid = data?.unpaid || [];

  return (
    <div data-testid="admin-billing">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Abonnements & impayés</h1>
      <p className="mt-1 text-sm text-slate-500">Suivi des abonnements Stripe (source de vérité) et gestion des impayés</p>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard testId="billing-mrr" icon={TrendingUp} tone="bg-emerald-50 text-emerald-600" label="MRR estimé" value={eur(data?.mrr_eur)} />
        <StatCard testId="billing-active" icon={CreditCard} tone="bg-indigo-50 text-indigo-600" label="Abonnements affichés" value={subs.length} />
        <StatCard testId="billing-unpaid" icon={AlertTriangle} tone="bg-rose-50 text-rose-600" label="Impayés" value={unpaid.length} />
      </div>

      {/* Impayés — section prioritaire */}
      <section className="mt-6 rounded-2xl border border-rose-200 bg-rose-50/40 p-5" data-testid="admin-billing-unpaid-section">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-rose-600"><AlertTriangle className="h-4 w-4" /> Impayés</h2>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : unpaid.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-testid="admin-billing-unpaid-empty">Aucun impayé. 🎉</p>
          : (
          <div className="overflow-x-auto rounded-xl border border-rose-100 bg-white">
            <table className="w-full text-sm" data-testid="admin-billing-unpaid-table">
              <thead className="bg-rose-50/60 text-left text-xs uppercase tracking-wide text-rose-400">
                <tr>{["Organisation", "Plan", "Ancienneté", "Grâce restante", "Statut", ""].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-rose-50">
                {unpaid.map((o) => {
                  const gd = daysUntil(o.grace_ends_at); const age = daysSince(o.past_due_since);
                  const urgent = gd !== null && gd <= 2;
                  return (
                    <tr key={o.id} data-testid={`unpaid-row-${o.id}`} className={urgent ? "bg-rose-50/60" : ""}>
                      <td className="px-4 py-3 font-medium text-slate-800">{o.name}</td>
                      <td className="px-4 py-3 capitalize text-slate-600">{PLAN_LABEL[o.plan] || o.plan}</td>
                      <td className="px-4 py-3 text-slate-600">{age !== null ? `${age} j` : "—"}</td>
                      <td className="px-4 py-3">{o.restricted ? <span className="rounded bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white">Grâce dépassée</span> : <span className={`font-semibold ${urgent ? "text-rose-600" : "text-slate-700"}`} data-testid={`unpaid-grace-${o.id}`}>{gd !== null ? `${gd} j` : "—"}</span>}</td>
                      <td className="px-4 py-3"><SubStatusBadge status={o.subscription_status} /></td>
                      <td className="px-4 py-3"><button onClick={() => navigate(`/admin/organisations/${o.id}`)} data-testid={`unpaid-open-${o.id}`} className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">Fiche <ArrowRight className="h-3 w-3" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tous les abonnements */}
      <div className="mb-4 mt-6 flex flex-wrap items-center gap-2">
        <FieldSelect testId="admin-billing-filter-plan" className="w-40" value={plan} onChange={setPlan} options={PLAN_OPTS} />
        <FieldSelect testId="admin-billing-filter-status" className="w-44" value={status} onChange={setStatus} options={STATUS_OPTS} />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={unpaidOnly} onChange={(e) => setUnpaidOnly(e.target.checked)} data-testid="admin-billing-unpaid-toggle" className="h-4 w-4 rounded border-slate-300" /> Impayés uniquement
        </label>
      </div>

      {isLoading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : subs.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-14 text-center text-sm text-slate-500" data-testid="admin-billing-empty">Aucun abonnement Stripe ne correspond.</div>
        : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-billing-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>{["Organisation", "Plan", "Montant/mois", "Statut", "Prochaine échéance", "Impayé depuis", "Grâce", "Résiliation"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subs.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/admin/organisations/${o.id}`)} data-testid={`billing-row-${o.id}`} className="cursor-pointer hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{o.name}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{PLAN_LABEL[o.plan] || o.plan}</td>
                  <td className="px-4 py-3 text-slate-700">{eur(o.price_eur)}</td>
                  <td className="px-4 py-3"><SubStatusBadge status={o.subscription_status} /></td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(o.current_period_end)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(o.past_due_since)}</td>
                  <td className="px-4 py-3 text-slate-500">{o.grace_ends_at ? (o.restricted ? "Dépassée" : `${daysUntil(o.grace_ends_at)} j`) : "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{o.cancel_at_period_end ? "Programmée" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
